import React, { useState, useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { HiOutlineUserGroup, HiSparkles, HiOutlineHeart, HiHeart, HiOutlineBriefcase, HiOutlineBuildingOffice2, HiOutlineMapPin, HiOutlineCalendarDays, HiOutlineCurrencyDollar, HiOutlineArrowTopRightOnSquare, HiOutlineEnvelope, HiOutlineChevronDown, HiOutlineChevronUp } from 'react-icons/hi2';
import { IoIosStar, IoIosStarOutline } from 'react-icons/io';
import { MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';
import { FaXmark } from 'react-icons/fa6';
import OutreachNavigator from './OutreachNavigator';
import OutreachCoPilot from './OutreachCoPilot';
import '../styles/JobTitles.css';
import * as Sentry from '@sentry/react';

function JobTitles({
                    user = {}, //User object
                    jobs = [], //Jobs list
                    total_jobs = 0, //Total jobs
                    finding = true, //True if on discover page (selected_filters == 'All'), False if not
                    setLoginRefresh = (()=>{}), //Set to true to trigger a refresh of jobs on page after login or logout
                    loginRefresh = false,
                    setUser = ((prev)=>{}), //Small frontend update on user object based on frontend action
                    setJobs = ((prev) => {}), //Small frontend update on jobs list based on frontend action
                    setTotalJobs = ((prev) => {}), //Small frontend update on jobs list based on frontend action
                    refreshJobs=(()=>{}), //Refresh jobs on load more
                    setFirstWait=()=>{}, //Set page loading
                    setPremiumPopup=()=>{}, //Show premium popup
                    setErrorMessage=()=>{}, //Set error message
                    setShowErrorPopup=()=>{}, //Show the error popup
                    selectedJobType = '',
                    setFiltersDisabled = () => {},
                    favoriteCompanyIds = [], //List of favorite companies
                    onToggleFavoriteCompany = () => {}, //Toggle favorite company
                    clearCompanyFilters = () => {}, //Clear company filters to show all jobs again
                    selectedCompanyIds = [], //List of selected company ids to filter jobs by
                    onRefreshCompanies = () => {}, //Refresh all companies list
                    favoritesSubTab = 'jobs', // 'jobs' or 'companies'
                }) {
    const [preDesc, setPreDesc] = useState([]); //Jobs waiting for backend response on full description
    const [showDesc, setShowDesc] = useState([]); //Jobs ready to display backend response on full description
    const [fullDesc, setFullDesc] = useState({}); //Dictionary of full descriptions (key: jobid, value: full desc)
    const [loadingMore, setLoadingMore] = useState(false); //In the process of loading more
    const [loadFails, setLoadFails] = useState([]); //collection of logos that failed to load
    const [limit, setLimit] = useState(25); //Current limit of jobs, starts at 25
    const [segments, setSegments] = useState(1); //Current segments of jobs, starts at 1
    const [removePopupJob, setRemovePopupJob] = useState(null); // null or job object
    const [removeReason, setRemoveReason] = useState('');        // selected reason
    const [fadingOutJobIds, setFadingOutJobIds] = useState([]);
    const [pendingApplyPopupJob, setPendingApplyPopupJob] = useState(null);  // job object for pending apply popup
    const [showApplyPopup, setShowApplyPopup] = useState(false);  // whether to show the apply popup
    // Outreach Co-Pilot state
    const [showNavigator, setShowNavigator] = useState(false);
    const [showCoPilot, setShowCoPilot] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [messageDisabled, setMessageDisabled] = useState({}); // { [jobIdStr]: true }
    const [msgCount, setMsgCount] = useState({}); // { [jobIdStr]: number }
    const [msgClicksCount, setMsgClicksCount] = useState({}); // total message button clicks

    const [currentSort, setCurrentSort] = useState(user.filters?.sort_by || "Date");

    const [notesDrafts, setNotesDrafts] = useState({}); // { [jobIdStr]: string }
    const [notesSaveState, setNotesSaveState] = useState({});
    const [statusFilter, setStatusFilter] = useState(
      ['applied', 'interviewing', 'offer', 'accepted', 'rejected'] // default = All
    );
    const [notesSavedTimeById, setNotesSavedTimeById] = useState({}); // { [jobIdStr]: isoString }


    const formatSavedTimeLocal = (isoUtc) => {
      if (!isoUtc) return "";
      const d = new Date(isoUtc);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    };

    const MAX_FREE_FAVORITE_COMPANIES = 3;

    const isPremiumUser = user?.plan === "premium";
    const hasReachedFreeFavoriteLimit =
        !isPremiumUser &&
        (favoriteCompanyIds?.length || 0) >= MAX_FREE_FAVORITE_COMPANIES;

    const formatCompanySize = (count) => {
        const num = Number(count);

        if (!num || num <= 0) return null;

        if (num <= 10) return "1–10";
        if (num <= 50) return "11–50";
        if (num <= 200) return "51–200";
        if (num <= 500) return "201–500";
        if (num <= 1000) return "501–1000";
        if (num <= 5000) return "1001–5000";
        if (num <= 10000) return "5001–10,000";

        return "10,000+";
    };

    // Check if a company is in the favorites list
    const isCompanyFavorited = (company_id) => {
        return favoriteCompanyIds.includes(company_id)
    };


    const closeNavigator = (opts = {}) => {
      setShowNavigator(false);
      if (!opts.preserveJob) setSelectedJob(null);
    };

    const STATUS_OPTIONS = [
      { key: 'applied',       label: 'Applied' },
      { key: 'interviewing',  label: 'Interviewing' },
      { key: 'offer',         label: 'Offer Received' },
      { key: 'accepted',      label: 'Accepted' },
      { key: 'rejected',      label: 'Rejected' },
    ];


    const handleDraftGenerated = (jobId, message) => {
      const idStr = String(jobId);

      // 1) disable the button for this job
      setMessageDisabled(prev => ({ ...prev, [idStr]: true }));

      // 2) optimistic update to user.messages_generated so the card shows it immediately
      setUser(prev => {
        const prevList = Array.isArray(prev?.messages_generated) ? prev.messages_generated : [];
        const idx = prevList.findIndex(m => String(m.job_id ?? m.id) === idStr);

        const nextList = idx >= 0
          ? prevList.map((m, i) => i === idx ? { ...m, message } : m) // update existing
          : [...prevList, { id: jobId, message }];                    // or append

        return { ...prev, messages_generated: nextList };
      });
    };

    const hasGeneratedMessage = (jobish) => {
      if (!jobish) return false;
      const idStr = String(jobish.id ?? jobish.job_id);
      if (!idStr) return false;

      // trust explicit list if present
      if (Array.isArray(user?.messages_generated)) {
        const found = user.messages_generated.some(m =>
          String(m.job_id ?? m.id) === idStr
        );
        if (found) return true;
      }

      // fall back to count map
      return (msgCount?.[idStr] || 0) >= 1;
    };


    useEffect(() => {
      if (!Array.isArray(user?.messages_generated)) return;

      const ids = user.messages_generated
        .map(it => (typeof it === "object" ? (it.job_id ?? it.id) : it))
        .filter(Boolean)
        .map(String);

      const counts = ids.reduce((acc, id) => {
        acc[id] = (acc[id] || 0) + 1;
        return acc;
      }, {});

      setMsgCount(counts);
    }, [user?.messages_generated]);


    const handleRemoveJobSubmit = async () => {
      if (!removeReason) {
        alert("Please select a reason.");
        return;
      }

      // Remove all jobs from this company
      if (removeReason === "Company") {
        const companyJobs = jobs.filter(
          (job) => job.company_id === removePopupJob.company_id
        );

        const companyJobIds = companyJobs.map((job) => job.id);

        setFadingOutJobIds(companyJobIds);

        setTimeout(() => {
          setUser((prev) => {
            const existingRemovedJobs = prev.removed_jobs || [];
            const existingRemovedCompanies = prev.removed_companies || [];

            const existingRemovedIds = new Set(
              existingRemovedJobs.map((job) => job.id)
            );

            const newRemovedJobs = companyJobs.filter(
              (job) => !existingRemovedIds.has(job.id)
            );

            return {
              ...prev,
              removed_jobs: [...existingRemovedJobs, ...newRemovedJobs],
              removed_companies: [...existingRemovedCompanies, removePopupJob.company_id],
            };
          });

          setTotalJobs(prev => Math.max(0, prev - companyJobs.length));
          setRemovePopupJob(null);
          setRemoveReason("");
          setFadingOutJobIds([]);
        }, 600);

        try {
          const resp = await fetch("/api/remove_company", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: removePopupJob.company_id,
            }),
          });
          const data = await resp.json();
          if (!resp.ok) {
            if (data.error_code === 'session_expired') {
              window.location.href = '/';
              return;
            }
            setErrorMessage(data.error_message || 'Failed to remove company.');
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 3000);
          }
        } catch (err) {
          Sentry.captureException(err);
          setErrorMessage('Failed to remove company.');
          setShowErrorPopup(true);
          setTimeout(() => setShowErrorPopup(false), 3000);
        }

        return;
      }

      // Default single-job behavior
      setFadingOutJobIds([removePopupJob.id]);

      setTimeout(() => {
        setUser((prev) => {
          const existingRemovedJobs = prev.removed_jobs || [];
          const alreadyRemoved = existingRemovedJobs.some(
            (job) => job.id === removePopupJob.id
          );

          return {
            ...prev,
            removed_jobs: alreadyRemoved
              ? existingRemovedJobs
              : [...existingRemovedJobs, removePopupJob],
          };
        });

        setTotalJobs(prev => prev >= 300 ? prev : Math.max(0, prev - 1));
        setRemovePopupJob(null);
        setRemoveReason("");
        setFadingOutJobIds([]);
      }, 600);

      try {
        const resp = await fetch("/api/remove_job", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            job_id: removePopupJob.id,
            reason: removeReason,
          }),
        });
        const data = await resp.json();
        if (!resp.ok) {
          if (data.error_code === 'session_expired') {
            window.location.href = '/';
            return;
          }
          setErrorMessage(data.error_message || 'Failed to remove job.');
          setShowErrorPopup(true);
          setTimeout(() => setShowErrorPopup(false), 3000);
        }
      } catch (err) {
        setErrorMessage('Failed to remove job.');
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
        Sentry.captureException(err);
      }
    };

    const advertisePremium = () => {
        //navigate("/plans?redirect=results"); OPTION 1: just navigate them to the plans page
        setPremiumPopup(true);
    }

    const doRefresh = async () => {
        setFirstWait(false);
        setFiltersDisabled(true);

        try {
            const response = await fetch('/api/refresh_jobs');
            const data = await response.json();

            if (!response.ok) {
                if (data.error_code === 'session_expired') {
                    window.location.href = '/';
                    return;
                }
                setErrorMessage(data.error_message || 'Error refreshing jobs. Please try again.');
                setShowErrorPopup(true);
                setTimeout(() => setShowErrorPopup(false), 4000);
                return;
            }

            if (data.jobs) setJobs(data.jobs);
            if (data.total_jobs != null) setTotalJobs(data.total_jobs);
            if (data.user) setUser(data.user);
            onRefreshCompanies();
        } catch (error) {
            Sentry.captureException(error);
            setErrorMessage('Error refreshing jobs. Please try again.');
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 4000);
        } finally {
            setFirstWait(true);
            setFiltersDisabled(false);
        }
    };


    useEffect(() => {
        if (loginRefresh) {
            doRefresh();
        }
        setLoginRefresh(false);
    }, []);

    const updateFilters = async (parameter, value) => {
        setFirstWait(false);
        setFiltersDisabled(true);

        try {
            const response = await fetch(`/api/update_filters?parameter=${parameter}&value=${value}`);
            const data = await response.json();

            if (!response.ok) {
                if (data.error_code === 'session_expired') {
                    window.location.href = '/';
                    return;
                }
                setErrorMessage(data.error_message || 'Error updating filters. Please try again.');
                setShowErrorPopup(true);
                setTimeout(() => setShowErrorPopup(false), 4000);
                return;
            }

            if (data.jobs) {
                refreshJobs(data.jobs, data.user, data.total_jobs);
            }
            if (data.error_message) {
                setErrorMessage(data.error_message);
                setShowErrorPopup(true);
                setTimeout(() => setShowErrorPopup(false), 4000);
            }
        } catch (error) {
            setErrorMessage('Error updating filters. Please try again.');
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 4000);
            Sentry.captureException(error);
        } finally {
            setFirstWait(true);
            setFiltersDisabled(false);
        }
    };

    const sortBy = () => {
        const sortValue = document.querySelector('input[name="sort"]:checked').value;
        updateFilters('sort_by', sortValue)
    }

    const recalibrateSearch = () => {
        clearCompanyFilters()
        updateFilters('reset', 'reset')
    }

    const ImageWithFailsafe = ({logo, letter}) => {
        if (logo) {
            return (loadFails.includes(logo) ?
                <div className="fallback-logo"> { letter } </div>
                :
                <img src={ logo } alt="Company Logo" className="company-logo"
                  onLoad={() => {}}
                  onError={() => {
                     setLoadFails(prev => [...prev, logo]);
                  }}
                />
              );
        } else {
            return <div className="fallback-logo">
            { letter }
        </div>;
        }
    }

    async function addToFavoritesPost(job_id) {
        const isFavorite = user.favorites.map(val => val.id).includes(job_id);
        const onFavoritesTab = user.filters?.selected_filter === 'Favorites';

        if (isFavorite && onFavoritesTab && favoritesSubTab === 'jobs') {
            // Fade out only when removing a favorite on the Jobs side of the Favorites tab
            setFadingOutJobIds([job_id]);
            setTimeout(() => {
                setUser(prev => ({
                    ...prev,
                    favorites: prev.favorites.filter(val => val.id !== job_id)
                }));
                setFadingOutJobIds([]); // Reset fade
            }, 400);
        } else {
            // Optimistically update favorites list
            setUser(prev => ({
                ...prev,
                favorites: isFavorite
                    ? prev.favorites.filter(val => val.id !== job_id)
                    : [...prev.favorites, jobs.find(val => val.id === job_id)]
            }));
        }

        try {
            const response = await fetch('/api/add_favorite', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ job_id }),
            });

            const result = await response.json();

            if (result.error_code === 'session_expired') {
                window.location.href = '/';
                return;
            }

            if (result.error_message) {
                // Revert UI change
                setUser(prev => ({
                    ...prev,
                    favorites: isFavorite
                        ? [...prev.favorites, jobs.find(val => val.id === job_id)]
                        : prev.favorites.filter(val => val.id !== job_id)
                }));
                setErrorMessage(result.error_message);
                setShowErrorPopup(true);
                setTimeout(() => setShowErrorPopup(false), 2000);
            }
        } catch (error) {
            // You can also show a fallback error here
            Sentry.captureException(error);
            setErrorMessage("Something went wrong.");
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 2000);
        }
    }




    async function addToAppliedPost(job_id) {
        const isApplied = user.applied_to.map(val => val.id).includes(job_id);
        const jobObject = jobs.find(val => val.id === job_id);

        // Optimistic UI: start fade out (not on Favorites tab — cards there shouldn't disappear on apply)
        const onFavoritesTab = user.filters?.selected_filter === 'Favorites';
        if (!onFavoritesTab) setFadingOutJobIds([job_id]);

        setTimeout(() => {
            // Update frontend state optimistically
            setUser(prev => ({
                ...prev,
                applied_to: isApplied
                    ? prev.applied_to.filter(val => val.id !== job_id)
                    : [...prev.applied_to, jobObject]
            }));
            setTotalJobs(prev => isApplied ? prev + 1 : (prev >= 300 ? prev : Math.max(0, prev - 1)));
            setFadingOutJobIds([]); // Reset fade
        }, 600);

        try {
            const response = await fetch('/api/add_applied_to', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ job_id }),
            });

            const result = await response.json();

            if (result.error_code === 'session_expired') {
                window.location.href = '/';
                return;
            }

            if (result.error_message) {
                // Revert UI update
                setUser(prev => ({
                    ...prev,
                    applied_to: isApplied
                        ? [...prev.applied_to, jobObject]
                        : prev.applied_to.filter(val => val.id !== job_id)
                }));
                setTotalJobs(prev => isApplied ? (prev >= 300 ? prev : Math.max(0, prev - 1)) : prev + 1);
                setErrorMessage(result.error_message);
                setShowErrorPopup(true);
                setTimeout(() => setShowErrorPopup(false), 2000);
            }
        } catch (error) {
            // Show fallback popup
            setErrorMessage("Something went wrong.");
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 2000);
            Sentry.captureException(error);
        }
    }

    async function updateAppliedStatus(job_id, newStatus) {
      try {
        const response = await fetch('/api/update_applied_status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            job_id,
            status: newStatus,  // e.g. 'applied', 'interviewing', 'offer', 'accepted', 'rejected'
          }),
        });

        const result = await response.json();

        if (result.error_code === 'session_expired') {
          window.location.href = '/';
          return;
        }

        if (result.error_message) {
          setErrorMessage(result.error_message);
          setShowErrorPopup(true);
          setTimeout(() => setShowErrorPopup(false), 2000);
        }
      } catch (error) {
        // Network / unexpected error
        setErrorMessage('Something went wrong updating job status.');
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 2000);
        Sentry.captureException(error);
      }
    }

    async function saveNotesToJob(job_id, notes, jobIdStr) {
      // Safety: enforce char limit before sending
      if (notes.length > 300) {
        setErrorMessage("Notes must be 300 characters or less.");
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 2000);
        Sentry.captureException(new Error("Notes length exceeded 300 characters."));
        return;
      }

      try {
        const response = await fetch('/api/save_notes_to_job', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            job_id: job_id,
            notes: notes,
          }),
        });

        const result = await response.json();

        if (result.error_code === 'session_expired') {
          window.location.href = '/';
          return;
        }

        if (result.status === 'success') {
          const savedIso = result.notes_saved_time || null;

          // Update the job object locally so persistedNotes matches
          setJobs(prev =>
            prev.map(j =>
              j.id === job_id ? { ...j, user_notes: notes, notes_saved_time: savedIso } : j
            )
          );

          // update UI timestamp source-of-truth immediately (prevents old timestamp flash)
          if (savedIso) {
            setNotesSavedTimeById(prev => ({ ...prev, [jobIdStr]: savedIso }));
          } else {
            setNotesSavedTimeById(prev => {
              const next = { ...prev };
              delete next[jobIdStr];
              return next;
            });
          }

          setNotesSaveState(prev => ({
            ...prev,
            [jobIdStr]: 'saved',
          }));
        } else {
          setNotesSaveState(prev => ({ ...prev, [jobIdStr]: 'unsaved' }));
          setErrorMessage(result.error_message || "Unable to save notes.");
          setShowErrorPopup(true);
          setTimeout(() => setShowErrorPopup(false), 2000);
        }
      } catch (error) {
        setNotesSaveState(prev => ({ ...prev, [jobIdStr]: 'unsaved' }));
        setErrorMessage("Something went wrong saving notes.");
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 2000);
        Sentry.captureException(error);
      }
    }

    // Toggle a single status in the filter
    const toggleStatusFilter = (key) => {
      setStatusFilter(prev => {
        if (prev.includes(key)) {
          // remove it
          return prev.filter(k => k !== key);
        } else {
          // add it
          return [...prev, key];
        }
      });
    };

    // Toggle the "All" checkbox
    const toggleAllStatuses = (checked) => {
      if (checked) {
        // select all statuses
        setStatusFilter(STATUS_OPTIONS.map(opt => opt.key));
      } else {
        // clear all (no jobs will show)
        setStatusFilter([]);
      }
    };

    // const nextrefresh = new Date(new Date(user.last_refresh).getTime() + 1000 * 60 * 60 * 24 * 7) 1000 * 60 * 1
    let user_last_refreshISO;
    let nextrefresh;

    if (user.last_refresh) {
        user_last_refreshISO = user.last_refresh.replace(' ', 'T').split('.')[0] + 'Z';
    } else {
        // Default to current time if user.last_refresh is null
        user_last_refreshISO = new Date().toISOString();
    }

    // Next refresh = user_last_refresh + 7 days
    nextrefresh = new Date(new Date(user_last_refreshISO).getTime() + 1000 * 60 * 60 * 24 * 7);

    // --- time helpers ---
    const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

    let timeAgoText = "";
    let withinTwoHours = false;

    if (user.last_refresh) {
      const lastRefreshDate = new Date(user_last_refreshISO);
      const now = new Date();
      const diffMs = now - lastRefreshDate;

      withinTwoHours = diffMs < TWO_HOURS_MS;

      if (withinTwoHours) {
        timeAgoText = "Jobs Up to Date!";
      } else {
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        timeAgoText = `Jobs updated ${diffDays} day${diffDays !== 1 ? 's' : ''}, ${diffHours} hr${diffHours !== 1 ? 's' : ''} ago`;
      }
    }

    // Keep your existing 7‑day rule, but also block refresh if within 2 hours
    const finalCanRefresh = (user.plan === "premium" || new Date() >= nextrefresh) && !withinTwoHours;

    const filteredJobs = useMemo(() => {
      let list = jobs.filter(job => !user.removed_jobs?.some(j => j.id === job.id));

      if (user.filters?.selected_filter === 'Applied_to') {
        list = list.filter(job => {
          const statusKey = (job.user_status || 'applied').toLowerCase();
          return statusFilter.includes(statusKey);
        });
      }

      return list;
    }, [jobs, user.removed_jobs, user.filters?.selected_filter, statusFilter]);

    const appliedIds = new Set((user?.applied_to ?? []).map(apil => apil?.id));
    const favoriteIds = new Set((user?.favorites ?? []).map(val => val?.id));

    const visibleJobs = filteredJobs
        .filter(job => !(user?.removed_jobs ?? []).some(j => j.id === job.id))
        .filter(job => {
            if (user?.filters?.selected_filter !== "Applied_to") return true;

            const statusKey = String(job?.user_status ?? "applied").toLowerCase();
            const safeStatusFilter = Array.isArray(statusFilter) ? statusFilter : [];
            return safeStatusFilter.includes(statusKey);
        })
        .filter(job => {
            const shouldHide =
                (finding && appliedIds.has(job.id));

            return !shouldHide;
        })
        .slice(0, limit);


    return (<>
        {/* Status Filter - only on Applied_to tab */}
        {user.filters?.selected_filter === 'Applied_to' && (
          <div className="applied-status-filter">
            <span className="applied-status-filter-label"><strong>Filter by status:</strong></span>

            <label className="applied-status-filter-item">
              <input
                type="checkbox"
                checked={statusFilter.length === STATUS_OPTIONS.length}
                onChange={(e) => toggleAllStatuses(e.target.checked)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    toggleAllStatuses(statusFilter.length !== STATUS_OPTIONS.length);
                  }
                }}
              />
              All
            </label>

            {STATUS_OPTIONS.map(opt => (
              <label key={opt.key} className="applied-status-filter-item">
                <input
                  type="checkbox"
                  checked={statusFilter.includes(opt.key)}
                  onChange={() => toggleStatusFilter(opt.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      toggleStatusFilter(opt.key);
                    }
                  }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        )}
        {selectedJobType === 'internships' && (
          <p>
            {finding ? "Internships Found:" : "Total:"}{" "}
            {user.filters?.selected_filter === 'Applied_to'
              ? filteredJobs.length
              : total_jobs < 300
                ? total_jobs
                : "300+"}
          </p>
        )}

        {selectedJobType === 'new_grad' && (
          <p>
            {finding ? "Jobs Found:" : "Total:"}{" "}
            {user.filters?.selected_filter === 'Applied_to'
              ? filteredJobs.length
              : total_jobs < 300
                ? total_jobs
                : "300+"}
          </p>
        )}
        {finding &&
        <div className="inline-buttons-container">
            <form id="sortFilterForm" method="post" action="{{ url_for('update_filters') }}">
                <input type="hidden" name="parameter" value="sort_buttons" />
                <div className="sort-buttons">
                    <label style={{cursor: "pointer"}}>
                        <input type="radio" name="sort" value="Date"
                          checked={currentSort === "Date"}
                          tabIndex={currentSort === "Date" ? -1 : 0}
                          style={{cursor: "pointer"}} disabled={!user.plan}
                          onChange={() => { setCurrentSort("Date"); sortBy(); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setCurrentSort("Date"); sortBy(); } }}
                        />
                        Sort by Date
                        <div className="tooltip">{"Sort jobs by the most recent date"}</div>
                    </label>
                    <label style={{cursor: "pointer"}}>
                        <input type="radio" name="sort" value="Relevance"
                          checked={currentSort === "Relevance"}
                          tabIndex={currentSort === "Relevance" ? -1 : 0}
                          style={{cursor: "pointer"}} disabled={!user.plan}
                          onChange={() => { setCurrentSort("Relevance"); sortBy(); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setCurrentSort("Relevance"); sortBy(); } }}
                        />
                        Sort by Relevance
                        <div className="tooltip">{"Sort jobs by our AI-powered matching algorithm"}</div>
                    </label>
                </div>
            </form>

            {user.filters && user.filters['selected_filter'] === 'All' &&
                <div className="refresh-jobs-container" style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: '10px' }}>
                    {/* Last refresh text */}
                        {user.last_refresh && (
                            <span style={{ fontSize: '0.9em', color: '#555' }}>
                                {timeAgoText}
                            </span>
                        )}
                        {/* Refresh button */}
                        <button
                          type="submit"
                          className={!finalCanRefresh ? "disabled-refresh-button" : "refresh-button"}
                          onClick={() => {
                            if (finalCanRefresh) {
                              doRefresh();
                            } else {
                              if (withinTwoHours) {
                                //
                              } else {
                                advertisePremium();
                              }
                            }
                          }}
                        >
                          <label style={{ cursor: finalCanRefresh ? "pointer" : "default" }}>
                            Refresh Jobs
                            {user.last_refresh !== null && (
                              <div className="tooltip">
                                {withinTwoHours ? (
                                  (() => {
                                    const last = new Date(user_last_refreshISO);
                                    const minsLeft = Math.max(
                                      0,
                                      Math.ceil((TWO_HOURS_MS - (new Date() - last)) / (1000 * 60))
                                    );
                                    return <span>Up to date. Try again in ~{minsLeft} min.</span>;
                                  })()
                                ) : (
                                  <>
                                    {finalCanRefresh ? (
                                      <span>{timeAgoText}</span>
                                    ) : (
                                      <>
                                        <p>Upgrade to premium to use this</p>
                                        <p>Next Refresh: {nextrefresh.toLocaleString()}</p>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </label>
                        </button>

                </div>
            }
        </div>
        }

    {visibleJobs.length === 0 && user?.filters?.selected_filter === "All" && (
        <div className="job-list-empty">
            <HiOutlineBriefcase size={40} />
            <h3>No Results Found</h3>
            <p>
                No results were found for your search. Try adjusting your filters or check back later for new job postings.
            </p>

            <br />

            <p>
                Not finding results? Try →{" "}
                <button
                    type="button"
                    className="recalibrate-search-link-btn"
                    onClick={recalibrateSearch}
                >
                    Recalibrate Search
                </button>
            </p>

            <br />

            <p>
                Something not working correctly? Contact us at support@rezify.ai
            </p>
        </div>
    )}

    {visibleJobs.length === 0 && user?.filters?.selected_filter === "Applied_to" && (
        <div className="job-list-empty">
            <HiOutlineBriefcase size={40} />
            <h3>No Applications</h3>
            <p>
                No applications yet. Mark jobs as "Applied To" to by clicking the checkbox in the top right corner of each job.
                Keep track of your job applications and filter them by status in this tab.
            </p>
        </div>
    )}

    {visibleJobs.map(job => {
        const selectedFilter = user.filters?.selected_filter;
        const recentlyAdded =
            selectedFilter !== 'Favorites' &&
            selectedFilter !== 'Applied_to' &&
            (Math.abs(new Date() - new Date(job.date_posted)) / 1000 / 60 / 60 / 24) < 3;

        const jobIdStr = String(job.id ?? job.job_id);
        const totalCount = msgCount[jobIdStr] || 0;
        const msgAlreadyGenerated = totalCount >= 1;

        const normalizedStatus = String(job.user_status || "applied").toLowerCase();

        const persistedNotes = job.user_notes ?? job.notes ?? "";
        const draft = notesDrafts[jobIdStr];
        const currentNotes = draft !== undefined ? draft : persistedNotes;

        const savedState = notesSaveState[jobIdStr];
        const isUnsaved = savedState
            ? savedState === "unsaved"
            : currentNotes !== persistedNotes;

        const savedIso = notesSavedTimeById[jobIdStr] ?? job.notes_saved_time ?? null;
        const savedTimeText = savedIso ? formatSavedTimeLocal(savedIso) : "";

        const isFavorited = isCompanyFavorited(job.company_id);

        const shouldDisableFavorite =
            !isFavorited && hasReachedFreeFavoriteLimit;

        return (
            <div
                style={{
                    backgroundColor: recentlyAdded
                        ? "color-mix(in srgb, white, var(--primary-color) 20%)"
                        : "white",
                }}
                className={`job-card ${fadingOutJobIds.includes(job.id) ? "fade-out" : ""}`}
            >
                {user?.applied_to && (
                    <div className="star-checkbox-container">
                        {/* The star icon to add job to favorites */}
                        <button
                            tabIndex={0}
                            className={"star" + (favoriteIds.has(job.id) ? " filled" : "")}
                            aria-label={favoriteIds.has(job.id) ? "Remove from favorites" : "Add to favorites"}
                            data-job-id={job.id}
                            onClick={() => {
                                addToFavoritesPost(job.id);
                            }}
                        >
                            {favoriteIds.has(job.id) ? <IoIosStar size={18} /> : <IoIosStarOutline size={18} />}
                        </button>
                        <div className="tooltip">{favoriteIds.has(job.id) ? "Remove From Favorites" : "Add Job To Favorites List"}</div>

                        {/* The check icon to mark job as applied to */}
                        <button
                            tabIndex={0}
                            className={"applied-check" + (appliedIds.has(job.id) ? " filled" : "")}
                            aria-label={appliedIds.has(job.id) ? "Mark as not applied" : "Mark as applied"}
                            data-job-id={job.id}
                            onClick={() => { addToAppliedPost(job.id); }}
                        >
                            {appliedIds.has(job.id) ? <MdCheckBox size={18} /> : <MdCheckBoxOutlineBlank size={18} />}
                        </button>
                        <div className="tooltip">{appliedIds.has(job.id) ? "Remove From Applied To" : "Mark Job As Applied To"}</div>

                        {/* The 'X' on the job card to remove it from the list */}
                        {user?.filters?.selected_filter === "All" && (
                            <>
                                <button
                                    tabIndex={0}
                                    className="remove-x"
                                    aria-label="Remove job from results"
                                    onClick={() => {
                                        if (
                                            (user && user.plan === "premium") ||
                                            (user && user.removed_jobs && user.removed_jobs.length < 5)
                                        ) {
                                            setRemovePopupJob(job);
                                        } else {
                                            advertisePremium();
                                        }
                                    }}
                                >
                                    <FaXmark size={18} />
                                </button>
                                <div className="tooltip">Remove This Job From Results</div>
                            </>
                        )}
                    </div>
                )}

                {/*=== Job Title & Icon ===*/}
                <div className="logo-title-container">
                    <div className="logo-with-favorite">
                        {job.company_url ?
                            <a href={ job.company_url } target="_blank" style={{'text-decoration': 'none', 'color': 'inherit'}}>
                                <ImageWithFailsafe  logo={job.company_logo} letter={(job.company_name || job.company || "?")[0]}/>
                            </a>
                        :
                        <>
                            <ImageWithFailsafe  logo={job.company_logo} letter={(job.company_name || job.company || "?")[0]}/>
                            </>
                        }
                        <button
                            className={`company-favorite-btn
                                ${isFavorited ? 'is-favorited' : ''}
                                ${shouldDisableFavorite ? 'is-disabled' : ''}`
                            }
                            onClick={(e) => {
                                e.stopPropagation();

                                if (shouldDisableFavorite) return;

                                onToggleFavoriteCompany(job.company_id, isFavorited);
                            }}
                            title={
                                shouldDisableFavorite
                                    ? "Free plan limit is 3 favorite companies. Upgrade to save more."
                                    : isFavorited
                                        ? "Remove company from favorites"
                                        : "Add company to favorites"
                            }
                            disabled={shouldDisableFavorite}
                        >
                            {isFavorited ? (
                                <HiHeart size={16} />
                            ) : (
                                <HiOutlineHeart size={16} />
                            )}
                        </button>
                    </div>

                    <h2 className="job-title">{job.title}</h2>

                    {/*=== Recently Added ===*/}
                    {recentlyAdded ? (
                        <h3 className="recently-posted-badge">⭐️ Recently Posted! ⭐️</h3>
                    ) : (
                        <></>
                    )}
                    {/*========================*/}
                </div>
                {/*========================*/}

                {/* Job Status Buttons - only show on "Applied_to" tab */}
                {user.filters?.selected_filter === "Applied_to" && (
                    <div className="job-status-container">
                        <div className="job-status-pills">
                            {STATUS_OPTIONS.map((opt) => (
                                <button
                                    key={opt.key}
                                    type="button"
                                    className={`status-pill ${normalizedStatus === opt.key ? "is-selected" : ""}`}
                                    onClick={() => {
                                        if (normalizedStatus === opt.key) return;

                                        job.user_status = opt.key; // Local visual update

                                        setJobs((prev) =>
                                            prev.map((j) =>
                                                j.id === job.id ? { ...j, user_status: opt.key } : j
                                            )
                                        );

                                        updateAppliedStatus(job.id, opt.key);
                                    }}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/*=== Job Metadata Grid ===*/}
                <div className="job-info-grid">
                    {/*=== Company ===*/}
                    <div className="job-meta-chip">
                        <HiOutlineBuildingOffice2 className="meta-icon" size={15} />
                        <span className="meta-value">
                            {job.company_url ? (
                                <a href={job.company_url} target="_blank" rel="noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
                                    {job.company_name || job.company}
                                </a>
                            ) : (job.company_name || job.company)}
                        </span>
                    </div>

                    {job.company_industry !== null && (
                        <div className="job-meta-chip">
                            <HiOutlineBriefcase className="meta-icon" size={15} />
                            <span className="meta-value">{job.company_industry}</span>
                        </div>
                    )}

                    {(() => {
                      const count = Number(job.company_employee_count);
                      const range = job.company_employee_count_range?.trim();
                      const sizeText = count > 0 ? formatCompanySize(count) : range?.length > 0 ? range : null;
                      if (!sizeText) return null;
                      return (
                        <div className="job-meta-chip">
                            <HiOutlineUserGroup className="meta-icon" size={15} />
                            <span className="meta-value">{sizeText}</span>
                        </div>
                      );
                    })()}

                    <div className="job-meta-chip">
                        <HiOutlineMapPin className="meta-icon" size={15} />
                        <span className="meta-value">
                            {job.location === "Remote"
                                ? job.location
                                : job.location && job.location.includes(",")
                                ? job.location
                                : `${job.location || "Unknown"}, ${job.state_code || ""}`}
                        </span>
                    </div>

                    <div className="job-meta-chip">
                        <HiOutlineCalendarDays className="meta-icon" size={15} />
                        <span className="meta-value">{job.date_posted}</span>
                    </div>

                    {job.salary && job.salary.length > 1 && (
                        <div className="job-meta-chip job-salary-chip">
                            <HiOutlineCurrencyDollar className="meta-icon" size={15} />
                            <span className="meta-value">{job.salary}</span>
                        </div>
                    )}
                </div>

                {/*=== Description toggle + Apply Here (same row) ===*/}
                <div className="desc-apply-row">
                    {job.description ? (
                        <span
                            className="more-link"
                            id={`more-link-${job.id}`}
                            tabIndex={0}
                            role="button"
                            onClick={() => {
                                if (!showDesc.includes(job.id)) {
                                    setShowDesc([...showDesc, job.id]);
                                } else {
                                    setShowDesc((prev) => prev.filter((val) => val !== job.id));
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    if (!showDesc.includes(job.id)) {
                                        setShowDesc([...showDesc, job.id]);
                                    } else {
                                        setShowDesc((prev) => prev.filter((val) => val !== job.id));
                                    }
                                }
                            }}
                        >
                            {showDesc.includes(job.id)
                                ? <><HiOutlineChevronUp size={14} /> Show Less</>
                                : <>Read full description <HiOutlineChevronDown size={14} /></>}
                        </span>
                    ) : (
                        <span
                            tabIndex={0}
                            role="button"
                            className="more-link"
                            id={`more-link-${job.id}`}
                            onClick={() => {
                                if (!showDesc.includes(job.id)) {
                                    setPreDesc((prev) => [...prev, job.id]);

                                    if (!(job.id in fullDesc)) {
                                        fetch(`/api/get_description/${job.id}`)
                                            .then(async (response) => {
                                                const data = await response.json();
                                                if (!response.ok) {
                                                    if (data.error_code === 'session_expired') {
                                                        window.location.href = '/';
                                                        return;
                                                    }
                                                    setFullDesc((prev) => ({
                                                        ...prev,
                                                        [job.id]: "<p>Could not load description.</p>",
                                                    }));
                                                    setShowDesc((prev) => [...prev, job.id]);
                                                    return;
                                                }
                                                if (data.status === "success") {
                                                    setFullDesc((prev) => ({
                                                        ...prev,
                                                        [job.id]: DOMPurify.sanitize(data.html),
                                                    }));
                                                    setShowDesc((prev) => [...prev, job.id]);
                                                } else {
                                                    setFullDesc((prev) => ({
                                                        ...prev,
                                                        [job.id]: "<p>Could not load description. Job may be expired</p>",
                                                    }));
                                                    setShowDesc((prev) => [...prev, job.id]);
                                                }
                                            })
                                            .catch(() => {
                                                setFullDesc((prev) => ({
                                                    ...prev,
                                                    [job.id]: "<p>Error loading description.</p>",
                                                }));
                                                setShowDesc((prev) => [...prev, job.id]);
                                            });
                                    } else {
                                        setShowDesc((prev) => [...prev, job.id]);
                                    }
                                } else {
                                    setShowDesc((prev) => prev.filter((val) => val !== job.id));
                                    setPreDesc((prev) => prev.filter((val) => val !== job.id));
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.currentTarget.click();
                                }
                            }}
                        >
                            {preDesc.includes(job.id)
                                ? showDesc.includes(job.id)
                                    ? <><HiOutlineChevronUp size={14} /> Show Less</>
                                    : <div className="spinner" />
                                : <>Read full description <HiOutlineChevronDown size={14} /></>}
                        </span>
                    )}

                    {/*=== Apply Here button (right side) ===*/}
                    <div className="apply-here-group">
                        {recentlyAdded && (
                            <em className="first-apply-badge">← Be first to apply!</em>
                        )}
                        <a
                            className="apply-here-btn"
                            href={job.final_url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => {
                                setTimeout(() => {
                                    setPendingApplyPopupJob(job);
                                    setShowApplyPopup(true);
                                }, 3000);
                            }}
                        >
                            <HiOutlineArrowTopRightOnSquare size={15} />
                            {user.filters?.selected_filter === "Applied_to"
                                ? "Application Link"
                                : "Apply Here"}
                        </a>
                    </div>
                </div>

                {/*=== Full description (below the row) ===*/}
                {job.description ? (
                    <div
                        className="full-description"
                        id={`full-description-${job.id}`}
                        style={{ display: showDesc.includes(job.id) ? "block" : "none" }}
                    >
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.description) }} />
                    </div>
                ) : (
                    <div
                        className="full-description"
                        id={`full-description-${job.id}`}
                        style={{ display: showDesc.includes(job.id) ? "block" : "none" }}
                    >
                        <div dangerouslySetInnerHTML={{ __html: fullDesc[job.id] }} />
                    </div>
                )}

                {job.recruiter_emails && (
                    <div className="job-meta-chip job-recruiter-chip">
                        <HiOutlineEnvelope className="meta-icon" size={15} />
                        <span className="meta-value">{job.recruiter_emails.join(", ")}</span>
                    </div>
                )}

                {/* Outreach Co-Pilot Section */}
                <div
                    className={
                        "outreach-section" +
                        (user.filters?.selected_filter === "Applied_to" ? " no-border" : "")
                    }
                >
                    <div className="outreach-row">

                        {/* LEFT SIDE: BUTTONS */}
                        <div className="outreach-buttons">
                            <button
                                type="button"
                                className="outreach-btn navigator-btn"
                                onClick={() => {
                                    setSelectedJob(job);
                                    setShowNavigator(true);
                                }}
                            >
                                <HiOutlineUserGroup size={16} />
                                Find Alumni on LinkedIn
                            </button>

                            {user.plan === "premium" ? (
                                <div
                                    className="copilot-btn-container"
                                    style={{ position: "relative", display: "inline-block" }}
                                >
                                    <button
                                        type="button"
                                        disabled={msgClicksCount[jobIdStr] >= 3 || !user.resume_json}
                                        title={
                                            !user.resume_json
                                                ? "Resume Data Not Gathered. Please re-enter a resume by going to your profile."
                                                : msgClicksCount[jobIdStr] >= 3
                                                ? "Limit Reached For This Job. Try again later"
                                                : messageDisabled[jobIdStr] || msgAlreadyGenerated
                                                ? "View the generated message again"
                                                : "Draft Message with Rezify"
                                        }
                                        className={`outreach-btn copilot-btn ${
                                            !user.resume_json || msgClicksCount[jobIdStr] >= 3
                                                ? "is-disabled"
                                                : ""
                                        }`}
                                        onClick={() => {
                                            if (!user.resume_json || msgClicksCount[jobIdStr] >= 3) return;

                                            if (!messageDisabled[jobIdStr] && !msgAlreadyGenerated) {
                                                setMsgClicksCount((prev) => ({
                                                    ...prev,
                                                    [jobIdStr]: (prev[jobIdStr] || 0) + 1,
                                                }));
                                            }

                                            setSelectedJob(job);
                                            setShowCoPilot(true);
                                        }}
                                        style={{
                                            opacity:
                                                !user.resume_json || msgClicksCount[jobIdStr] >= 3 ? 0.5 : 1,
                                            cursor:
                                                !user.resume_json || msgClicksCount[jobIdStr] >= 3
                                                    ? "not-allowed"
                                                    : "pointer",
                                            filter:
                                                !user.resume_json || msgClicksCount[jobIdStr] >= 3
                                                    ? "grayscale(100%)"
                                                    : "none",
                                        }}
                                    >
                                        <HiSparkles size={16} />
                                        {!user.resume_json
                                            ? "Draft Message with Rezify"
                                            : messageDisabled[jobIdStr] || msgAlreadyGenerated
                                            ? "View Generated Message"
                                            : "Draft Message with Rezify"}
                                    </button>

                                    {/* Tooltip */}
                                    {(!user.resume_json || msgClicksCount[jobIdStr] >= 3) && (
                                        <div className="tooltip">
                                            {!user.resume_json
                                                ? "Resume Data Not Gathered. Please re-enter a resume by going to your profile."
                                                : "Limit Reached For This Job. Try again later"}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="outreach-btn premium-btn"
                                    onClick={advertisePremium}
                                >
                                    <HiSparkles size={16} />
                                    Draft Message with Rezify
                                </button>
                            )}
                        </div>

                        {/* RIGHT SIDE: MATCH INDICATOR */}
                        {user.filters?.selected_filter === "All" &&
                            selectedCompanyIds.length > 0 &&
                            job.match_score &&
                            job.top_match && (
                                <span className="job-match-indicator">
                                    Match
                                </span>
                        )}

                    </div>
                </div>

                {/* Notes - only show on "Applied_to" tab */}
                {user.filters?.selected_filter === "Applied_to" && (
                    <div className="job-notes-container">
                        <label className="job-notes-label">Notes</label>
                        <textarea
                            className="job-notes-textarea"
                            aria-label="Job notes"
                            maxLength={300}
                            value={currentNotes}
                            onChange={(e) => {
                                const next = e.target.value.slice(0, 300);
                                setNotesDrafts((prev) => ({
                                    ...prev,
                                    [jobIdStr]: next,
                                }));

                                setNotesSaveState((prev) => ({
                                    ...prev,
                                    [jobIdStr]: "unsaved",
                                }));
                            }}
                            placeholder="Add notes about your application, interviews, timeline, etc."
                        />
                        <div className="job-notes-footer">
                            <div className="notes-left">
                                <span className="job-notes-counter">
                                    {currentNotes.length}/300
                                </span>

                                <span className={isUnsaved ? "notes-status unsaved" : "notes-status saved"}>
                                    {isUnsaved ? (
                                        "(unsaved)"
                                    ) : notesSaveState[jobIdStr] === "saving" ? (
                                        "(Saved • Just Now)"
                                    ) : savedTimeText ? (
                                        `(Saved • ${savedTimeText})`
                                    ) : (
                                        "(Saved)"
                                    )}
                                </span>
                            </div>

                            <button
                                type="button"
                                className="job-notes-save-button"
                                onClick={() => {
                                    const original = persistedNotes;
                                    if (currentNotes === original) return;

                                    setNotesSaveState((prev) => ({
                                        ...prev,
                                        [jobIdStr]: "saving",
                                    }));

                                    saveNotesToJob(job.id, currentNotes, jobIdStr);
                                }}
                            >
                                Save Notes
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    })}

    {showApplyPopup && pendingApplyPopupJob && (
      <div className="modal-overlay">
        <div className="modal-content">
          <h2>Did you apply?</h2>
          <p>
            Did you submit an application to <strong>{pendingApplyPopupJob.title}</strong> at <strong>{pendingApplyPopupJob.company_name || pendingApplyPopupJob.company}</strong>?
          </p>
          <div className="modal-buttons">
            <button
              className="submit-btn"
              onClick={() => {
                addToAppliedPost(pendingApplyPopupJob.id);
                setShowApplyPopup(false);
                setPendingApplyPopupJob(null);
              }}
            >
              Yes
            </button>
            <button
              className="cancel-btn"
              onClick={() => {
                setShowApplyPopup(false);
                setPendingApplyPopupJob(null);
              }}
            >
              No
            </button>
          </div>
        </div>
      </div>
    )}


    {removePopupJob && (
  <div className="modal-overlay">
    <div className="modal-content">
      <h2>Why do you want to remove this job?</h2>
      <p><strong>{removePopupJob.title}</strong> at <strong>{removePopupJob.company_name || removePopupJob.company}</strong></p>

      <select aria-label="Reason for removing job" value={removeReason} onChange={(e) => setRemoveReason(e.target.value)}>
        <option value="">Select a reason</option>
        <option value="Expired">Job is not available/expired</option>
        <option value="Scam">Job is a scam</option>
        <option value="Duplicate">Job is a duplicate of another</option>
        <option value="Bad Match">Does not match my search</option>
        <option value="Company">Remove all postings from this company</option>
        <option value="Other">Other</option>
      </select>

      <div className="modal-buttons">
            <button
            className="submit-btn"
            onClick={handleRemoveJobSubmit}>Submit</button>

            <button
            className="cancel-btn"
            onClick={() => {
              setRemovePopupJob(null);
              setRemoveReason('');
            }}>Cancel</button>
          </div>
        </div>
      </div>
    )}


    {user.id ?
    (user.plan !== "premium" ?
    <>
    <p style={{textAlign: 'center'}}>You've reached your limit of <span style={{color:'var(--primary-aw)'}}>25 results</span></p>
    <p style={{textAlign: 'center'}}><a style={{color:'var(--primary-aw)'}} href="/plans?redirect=results"><b>Upgrade</b></a> to view more...</p>
    </>
    :
    <div className="pagination">
        {(total_jobs > limit) &&
        <button onClick={(event) => {
            if (!loadingMore) {
                if (finding) {
                    setLoadingMore(true);
                    //ask backend for more
                    fetch(`/api/load_more?segments=${segments + 1}`)
                    .then(async response => {
                        const data = await response.json();
                        if (!response.ok) {
                            if (data.error_code === 'session_expired') {
                                window.location.href = '/';
                                return;
                            }
                            setErrorMessage(data.error_message || 'Error loading more results. Please try again.');
                            setShowErrorPopup(true);
                            setTimeout(() => setShowErrorPopup(false), 4000);
                            setLoadingMore(false);
                            return;
                        }
                        setJobs(data.jobs);
                        setLimit(prev => prev + 25); //Show 25 more on the frontend after receiving response
                        setLoadingMore(false);
                        setSegments(segments + 1);
                    })
                    .catch(error => {
                        setErrorMessage('Error loading more results. Please try again.');
                        setShowErrorPopup(true);
                        setTimeout(() => setShowErrorPopup(false), 4000);
                        setLoadingMore(false);
                        Sentry.captureException(error);
                    })
                } else {
                    //If not in discover, then we're really just upping the amount of jobs currently on display, no backend response needed
                    setLimit(prev => prev + 25); //Show 25 more on the frontend after receiving response
                }
            }
        }}>{loadingMore ? <div className='spinner'/> : "Load More Results"}</button>
        }
        <p>
          {user.filters?.selected_filter === "Applied_to" ? (
            <>
              {Math.min(limit, filteredJobs.length)} of {filteredJobs.length} results
            </>
          ) : (
            <>
              {limit > total_jobs ? total_jobs : limit} of {total_jobs} results
            </>
          )}
        </p>
    </div>
    )
    :
    <>
    <br/>
    <p style={{textAlign: 'center'}}>Register to view more...</p>
    </>
                }

    {/* Outreach Co-Pilot Modals */}
    <OutreachNavigator
      isOpen={showNavigator}
      onClose={() => closeNavigator()}
      onOpenCoPilot={(jobFromNav) => {
        setSelectedJob(jobFromNav);
        closeNavigator({ preserveJob: true });
        setShowCoPilot(true);
      }}
      job={selectedJob}
      user={user}
      hasMessage={hasGeneratedMessage(selectedJob)}
      setErrorMessage={setErrorMessage}
      setShowErrorPopup={setShowErrorPopup}
    />

    <OutreachCoPilot
      isOpen={showCoPilot}
      onClose={() => {
        setShowCoPilot(false);
        setSelectedJob(null);
      }}
      job={selectedJob}
      user={user}
      onDraftGenerated={handleDraftGenerated}
      hasMessage={hasGeneratedMessage(selectedJob)}
      setErrorMessage={setErrorMessage}
      setShowErrorPopup={setShowErrorPopup}
    />
    </>);
}

export {JobTitles}